import { releaseClientFetch } from '../app/release_client_state.js';
import { withWorkspaceLoadingOverlay } from '../ui/workspace_loading_overlay.js?v=20260826a';
import { AppRuntime } from '../app/app_runtime.js?v=20260706d';
import * as AppOptionsController from '../app/app_options_controller.js?v=20260904f';
import { setStatus } from '../render/plan_renderer.js?v=20260904f';
import * as SystemRegistry from '../registry/system_registry.js?v=20260904f';
import { openChoiceDialog } from '../ui/dialog_service.js?v=20260817b';
import { openUserQuickCreateModal } from '../app/lazy_dialog_runtime.js?v=20260904f';
import { getCurrentUser } from '../user/current_user.js?v=20260722a';
import { ensureColorCatalogsForProject } from '../../../colors/color_catalog_registry.js?v=20260720a';
import { S } from '../app/app_state.js?v=20260817c';
import {
  cloneDeep,
  recordUndo
} from '../services/history_service.js?v=20260903c';
import {
  getProjectModelRevision,
  markProjectModelChanged
} from './project_revision_service.js';
import {
  normalizeProjectCustomerIdentity,
  normalizeProjectState,
  projectCustomerIdForName,
  resetTransientProjectRuntimeState,
  syncProjectItems
} from './project_state_service.js?v=20260904f';
import {
  hasProjectDrawingContent as hasRegisteredProjectDrawingContent
} from './project_content_registry.mjs?v=20260904f';
import {
  createProjectDocument,
  finalizeProjectDocument,
  parseProjectDocument
} from './project_document.mjs';
import {
  anyDrawingSheetHasContent,
  resetDrawingSheets,
  restoreDrawingSheetsFromDocument,
  serializeDrawingSheets
} from './drawing_sheet_service.js?v=20260904c';
import * as OrderEditingSession from './order_editing_session.js?v=20260731a';

const DEFAULT_PROJECT_NUMBER = '101100000';
const DEFAULT_TEMP_USER_LABEL = 'Temp';
const AUTOSAVE_INTERVAL_MS = 15 * 60 * 1000;
const CHANGE_AUTOSAVE_DEBOUNCE_MS = 60 * 1000;
const LAST_OPEN_ORDER_STORAGE_KEY = 'ks-studio-last-open-order-v1';

let initialized = false;
let autosaveInFlight = false;
let saveRequestInFlight = false;
let autoOpenLastOrderAttempted = false;
let newDetachedProjectInFlight = false;
let initialDrawAutosaveTimer = 0;
let statisticsAdapterServicePromise = null;
let lastStatus = {
  state: 'dirty',
  label: 'Ungespeicherte Änderungen',
  lastSavedAt: null,
  error: ''
};

function normalizeText(value) {
  return String(value ?? '').trim();
}

async function collectManualStatisticsSnapshot(project) {
  try {
    if (!statisticsAdapterServicePromise) {
      statisticsAdapterServicePromise = import('../statistics/statistics_adapter_service.js?v=20260903a')
        .catch(error => {
          statisticsAdapterServicePromise = null;
          throw error;
        });
    }
    const module = await statisticsAdapterServicePromise;
    return await module.collectStatisticsSnapshot({ project });
  } catch (error) {
    console.warn('Statistik-Snapshot konnte nicht erstellt werden; der Auftrag wird ohne Snapshot gespeichert.', error);
    return null;
  }
}

function plainObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

function normalizeComparable(value) {
  return normalizeText(value).toLowerCase();
}

function positiveCustomerId(value) {
  const numeric = Number(value);
  return Number.isSafeInteger(numeric) && numeric > 0 ? numeric : null;
}

function normalizedPersonName(firstName, lastName, fallback = '') {
  const composed = [firstName, lastName]
    .map(value => normalizeText(value))
    .filter(Boolean)
    .join(' ');
  return composed || normalizeText(fallback);
}

function hasExplicitCustomerContactIdOption(options = {}) {
  return Object.prototype.hasOwnProperty.call(options, 'customerContactId')
    || Object.prototype.hasOwnProperty.call(options, 'customer_contact_id')
    || Object.prototype.hasOwnProperty.call(options, 'kunde_kontakt_id');
}

function customerContactIdentityForOptions(customerId, options = {}, project = S.project) {
  const sameCustomer = positiveCustomerId(customerId) === positiveCustomerId(project?.customerId);
  const explicit = hasExplicitCustomerContactIdOption(options);
  const customerContactId = positiveCustomerId(explicit
    ? (options.customerContactId ?? options.customer_contact_id ?? options.kunde_kontakt_id)
    : (sameCustomer ? project?.customerContactId : null));
  if (!positiveCustomerId(customerId) || !customerContactId) {
    return {
      customerContactId: null,
      customerContactFirstName: '',
      customerContactLastName: '',
      customerContactName: ''
    };
  }
  const sameContact = customerContactId === positiveCustomerId(project?.customerContactId);
  const customerContactFirstName = normalizeText(
    options.customerContactFirstName
    ?? options.customer_contact_first_name
    ?? (sameContact ? project?.customerContactFirstName : '')
  );
  const customerContactLastName = normalizeText(
    options.customerContactLastName
    ?? options.customer_contact_last_name
    ?? (sameContact ? project?.customerContactLastName : '')
  );
  const legacyName = normalizeText(
    options.customerContactName
    ?? options.customer_contact_name
    ?? (sameContact ? project?.customerContactName : '')
  );
  return {
    customerContactId,
    customerContactFirstName,
    customerContactLastName,
    customerContactName: normalizedPersonName(customerContactFirstName, customerContactLastName, legacyName)
  };
}

function customerContactRequestFields(identity, options = {}) {
  if (!hasExplicitCustomerContactIdOption(options) && !identity.customerContactId) return {};
  return {
    customerContactId: identity.customerContactId,
    customerContactFirstName: identity.customerContactFirstName,
    customerContactLastName: identity.customerContactLastName,
    customerContactName: identity.customerContactName
  };
}

function customerIdentityForName(customerName, project = S.project) {
  const customerId = projectCustomerIdForName(project, customerName);
  return {
    customerId,
    customerIdentityName: customerId
      ? normalizeText(project?.customerIdentityName || customerName)
      : ''
  };
}

function hasExplicitCustomerIdOption(options = {}) {
  return Object.prototype.hasOwnProperty.call(options, 'customerId')
    || Object.prototype.hasOwnProperty.call(options, 'customer_id')
    || Object.prototype.hasOwnProperty.call(options, 'kunde_id');
}

function customerIdentityForOptions(customerName, options = {}, project = S.project) {
  if (!hasExplicitCustomerIdOption(options)) return customerIdentityForName(customerName, project);
  const customerId = positiveCustomerId(
    options.customerId ?? options.customer_id ?? options.kunde_id
  );
  return {
    customerId,
    customerIdentityName: customerId ? normalizeText(customerName) : ''
  };
}

function customerIdRequestFields(customerIdentity, options = {}) {
  if (hasExplicitCustomerIdOption(options)) return { customerId: customerIdentity.customerId };
  return customerIdentity.customerId ? { customerId: customerIdentity.customerId } : {};
}

function applyOrderCustomerIdentity(order = {}, fallbackName = '') {
  const previousCustomerId = positiveCustomerId(S.project?.customerId);
  const customerName = normalizeText(order.customerName ?? order.kunde ?? fallbackName);
  const hasExplicitCustomerId = Object.prototype.hasOwnProperty.call(order, 'customerId')
    || Object.prototype.hasOwnProperty.call(order, 'customer_id')
    || Object.prototype.hasOwnProperty.call(order, 'kunde_id');
  const explicitCustomerId = positiveCustomerId(
    order.customerId ?? order.customer_id ?? order.kunde_id
  );
  const customerId = hasExplicitCustomerId
    ? explicitCustomerId
    : projectCustomerIdForName(S.project, customerName);
  S.project.customerName = customerName;
  S.project.customerId = customerId;
  S.project.customerIdentityName = customerId ? customerName : '';
  const hasExplicitContact = hasExplicitCustomerContactIdOption(order);
  const sameCustomer = previousCustomerId === customerId;
  const customerContactId = positiveCustomerId(hasExplicitContact
    ? (order.customerContactId ?? order.customer_contact_id ?? order.kunde_kontakt_id)
    : (sameCustomer ? S.project.customerContactId : null));
  const sameContact = customerContactId === positiveCustomerId(S.project?.customerContactId);
  const customerContactFirstName = customerId && customerContactId
    ? normalizeText(
        order.customerContactFirstName
        ?? order.customer_contact_first_name
        ?? (sameContact ? S.project.customerContactFirstName : '')
      )
    : '';
  const customerContactLastName = customerId && customerContactId
    ? normalizeText(
        order.customerContactLastName
        ?? order.customer_contact_last_name
        ?? (sameContact ? S.project.customerContactLastName : '')
      )
    : '';
  const customerContactName = customerId && customerContactId
    ? normalizedPersonName(
        customerContactFirstName,
        customerContactLastName,
        order.customerContactName
          ?? order.customer_contact_name
          ?? (sameContact ? S.project.customerContactName : '')
      )
    : '';
  S.project.customerContactId = customerId ? customerContactId : null;
  S.project.customerContactFirstName = customerContactFirstName;
  S.project.customerContactLastName = customerContactLastName;
  S.project.customerContactName = customerContactName;
  return {
    customerId,
    customerName,
    customerContactId: S.project.customerContactId,
    customerContactFirstName,
    customerContactLastName,
    customerContactName
  };
}

function currentUserLastOrderKey() {
  const user = getCurrentUser();
  const userId = normalizeText(user?.benutzer_id ?? user?.id);
  if (userId) return `id:${userId}`;
  const loginName = normalizeText(user?.login_name ?? user?.loginName).toLowerCase();
  if (loginName) return `login:${loginName}`;
  const email = normalizeText(user?.email).toLowerCase();
  return email ? `email:${email}` : '';
}

function readLastOpenOrderStore() {
  try {
    const raw = window.localStorage?.getItem(LAST_OPEN_ORDER_STORAGE_KEY);
    return raw ? plainObject(JSON.parse(raw)) : {};
  } catch (_err) {
    return {};
  }
}

function writeLastOpenOrderStore(store) {
  try {
    window.localStorage?.setItem(LAST_OPEN_ORDER_STORAGE_KEY, JSON.stringify(store));
  } catch (_err) {
    // Die automatische Wiederaufnahme ist Komfortfunktion; die App bleibt ohne Storage nutzbar.
  }
}

function orderRefFromMeta(meta = {}) {
  const order = meta?.order || meta;
  const projectNumber = normalizeText(order.projectNumber ?? order.projektnummer ?? S.project?.projectNumber);
  const orderNumber = normalizeText(order.orderNumber ?? order.auftragsnummer ?? S.project?.orderNumber);
  if (!projectNumber || !orderNumber) return null;
  return {
    projectNumber,
    orderNumber,
    orderId: order.auftragId ?? order.auftrag_id ?? S.project?.persistence?.auftragId ?? null,
    projectId: order.projektId ?? order.projekt_id ?? S.project?.persistence?.projektId ?? null,
    customerName: normalizeText(order.customerName ?? order.kunde ?? S.project?.customerName),
    customerId: positiveCustomerId(order.customerId ?? order.customer_id ?? order.kunde_id ?? S.project?.customerId),
    customerContactId: positiveCustomerId(
      order.customerContactId ?? order.customer_contact_id ?? order.kunde_kontakt_id ?? S.project?.customerContactId
    ),
    customerContactFirstName: normalizeText(
      order.customerContactFirstName ?? order.customer_contact_first_name ?? S.project?.customerContactFirstName
    ),
    customerContactLastName: normalizeText(
      order.customerContactLastName ?? order.customer_contact_last_name ?? S.project?.customerContactLastName
    ),
    customerContactName: normalizedPersonName(
      order.customerContactFirstName ?? order.customer_contact_first_name ?? S.project?.customerContactFirstName,
      order.customerContactLastName ?? order.customer_contact_last_name ?? S.project?.customerContactLastName,
      order.customerContactName ?? order.customer_contact_name ?? S.project?.customerContactName
    ),
    orderDescription: normalizeText(order.orderDescription ?? order.bezeichnung ?? S.project?.orderDescription),
    rememberedAt: new Date().toISOString()
  };
}

function rememberLastOpenedOrder(meta = {}) {
  const userKey = currentUserLastOrderKey();
  const orderRef = orderRefFromMeta(meta);
  if (!userKey || !orderRef) return null;
  const store = readLastOpenOrderStore();
  const users = { ...plainObject(store.users) };
  users[userKey] = orderRef;
  writeLastOpenOrderStore({ ...store, users });
  return orderRef;
}

function readLastOpenedOrder() {
  const userKey = currentUserLastOrderKey();
  if (!userKey) return null;
  const store = readLastOpenOrderStore();
  const orderRef = plainObject(plainObject(store.users)[userKey]);
  const projectNumber = normalizeText(orderRef.projectNumber);
  const orderNumber = normalizeText(orderRef.orderNumber);
  return projectNumber && orderNumber
    ? { ...orderRef, projectNumber, orderNumber }
    : null;
}

function blankDetachedProjectState() {
  const currentProject = plainObject(S.project);
  return {
    name: 'GMS Planner Projekt',
    systemType: 'multi_system',
    projectNumber: '',
    customerName: '',
    customerId: null,
    customerIdentityName: '',
    customerContactId: null,
    customerContactFirstName: '',
    customerContactLastName: '',
    customerContactName: '',
    orderNumber: '',
    orderDescription: '',
    seller: '',
    sellerUserId: null,
    avor: '',
    avorUserId: null,
    gridColor: currentProject.gridColor,
    gridSize: currentProject.gridSize,
    helperGridSize: currentProject.helperGridSize,
    showHelperGrid: currentProject.showHelperGrid,
    snap: currentProject.snap !== false,
    showGrid: currentProject.showGrid !== false,
    currentView: 'plan',
    planLayerVisibility: { schemaVersion: 1, groups: {}, layers: {} },
    persistence: {},
    floorplan: null,
    dimensions: [],
    textAnnotations: [],
    obstacles: [],
    objectAssets: [],
    items: [],
    bomOverrides: {
      manualItems: [],
      order: []
    }
  };
}

function isTempOrderNumber(value) {
  return /^temp[0-9]+$/i.test(normalizeText(value));
}

function hasProjectDrawingContent(project = S.project) {
  if (!project || typeof project !== 'object') return false;
  if (project === S.project) syncProjectItems();
  if (hasRegisteredProjectDrawingContent(project)) return true;
  return project === S.project && anyDrawingSheetHasContent();
}

// Auftragsdaten sind speicherbar, auch bevor die erste Geometrie entsteht.
// Automatisch vergebene TEMP-Nummern qualifizieren keinen leeren Arbeitsbereich.
function hasSavableProjectContent(orderNumber = S.project?.orderNumber) {
  const number = normalizeText(orderNumber);
  return hasProjectDrawingContent() || Boolean(number && !isTempOrderNumber(number));
}

async function confirmUnsavedDrawingBeforeReset() {
  if (!S.file?.dirty || !hasSavableProjectContent()) return 'continue';
  const choice = await openChoiceDialog({
    title: 'Ungespeicherte Zeichnung',
    message: 'Achtung, eine ungespeicherte Zeichnung ist offen. Was soll mit der aktuellen Zeichnung passieren?',
    defaultValue: 'cancel',
    choices: [
      { value: 'cancel', label: 'Abbrechen', autofocus: true },
      { value: 'discard', label: 'Verwerfen', className: 'danger' },
      { value: 'save', label: 'Speichern', className: 'primary' }
    ]
  });
  return choice || 'cancel';
}

function apiUrl(path, params = null) {
  const url = new URL(path, window.location.origin);
  if (params && typeof params === 'object') {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') url.searchParams.set(key, value);
    });
  }
  return url.href;
}

async function readJsonResponse(response) {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok || payload?.ok === false) {
    const error = new Error(payload?.error || payload?.message || `HTTP ${response.status}`);
    error.status = response.status;
    error.payload = payload;
    throw error;
  }
  return payload;
}

function ensureDefaultProjectContext() {
  if (!S.project || typeof S.project !== 'object') S.project = {};
  if (!normalizeText(S.project.projectNumber)) S.project.projectNumber = DEFAULT_PROJECT_NUMBER;
  if (typeof S.project.customerName !== 'string') {
    S.project.customerName = normalizeText(S.project.customerName ?? S.project.customer ?? S.project.kunde ?? '');
  }
  normalizeProjectCustomerIdentity(S.project);
  if (typeof S.project.orderNumber !== 'string') S.project.orderNumber = '';
  if (typeof S.project.orderDescription !== 'string') S.project.orderDescription = '';
  if (typeof S.project.seller !== 'string') S.project.seller = '';
  if (typeof S.project.avor !== 'string') S.project.avor = '';
  if (S.project.sellerUserId === undefined) S.project.sellerUserId = null;
  if (S.project.avorUserId === undefined) S.project.avorUserId = null;
  if (!S.project.persistence || typeof S.project.persistence !== 'object' || Array.isArray(S.project.persistence)) {
    S.project.persistence = {};
  }
}

function updateAutosaveStatus(nextStatus = {}) {
  lastStatus = {
    ...lastStatus,
    ...nextStatus
  };
  const target = document.getElementById('autosaveStatus');
  if (target) {
    target.textContent = lastStatus.label;
    target.dataset.state = lastStatus.state || '';
    target.title = lastStatus.error || lastStatus.label;
  }
}

function formatSavedTime(value) {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
}

// Solange kein explizites Projekt geoeffnet ist, zeichnet man immer auf einem
// (hoechstens) temporaeren Auftrag: entweder ohne Auftragsnummer, oder mit
// einer vom Backend automatisch vergebenen temp<Zahl>-Nummer. Erst eine im
// Dossier eingetragene eigene Auftragsnummer beendet den temporaeren Status.
function isCurrentOrderTemporary() {
  if (!hasProjectDrawingContent()) return false;
  const orderNumber = normalizeText(S.project?.orderNumber);
  return !orderNumber || isTempOrderNumber(orderNumber);
}

function syncTemporaryOrderHint() {
  const el = document.getElementById('temporaryOrderHint');
  if (!el) return;
  el.hidden = !isCurrentOrderTemporary();
}

function refreshDirtyStatus() {
  syncTemporaryOrderHint();
  if (lastStatus.state === 'saving' || lastStatus.state === 'loading' || lastStatus.state === 'error') return;
  if (S.file?.dirty) {
    updateAutosaveStatus({ state: 'dirty', label: 'Ungespeicherte Änderungen' });
    return;
  }
  if (lastStatus.lastSavedAt) {
    updateAutosaveStatus({
      state: 'saved',
      label: `Gespeichert ${formatSavedTime(lastStatus.lastSavedAt)}`
    });
  }
}

// War urspruenglich nur fuer den allerersten Save eines brandneuen Auftrags
// gedacht (250ms, danach per auftragId>0-Sperre dauerhaft deaktiviert).
// Nutzer-Meldung 2026-08-18: Aenderungen an bereits gespeicherten Auftraegen
// (z. B. Objektgroesse aendern) gingen dadurch faktisch bis zu 15 Minuten
// lang ungesichert, da danach nur noch der periodische AUTOSAVE_INTERVAL_MS-
// Timer lief. Sperre entfernt.
// Nutzer-Entscheidung 2026-08-31: Ein Autosave alle paar Sekunden bei jeder
// Aenderung ist zu haeufig -- 60s Verzoegerung reicht. Ausnahme: der
// allererste Save eines brandneuen, noch nie gespeicherten Auftrags (keine
// auftragId) soll weiterhin SOFORT passieren, nicht erst nach 60s, damit ab
// der ersten Zeichnung ohne Verzoegerung wenigstens ein temporaerer Auftrag
// existiert (siehe isCurrentOrderTemporary/temporaryOrderHint). Das ersetzt
// den vormaligen separaten 5-Sekunden-Poll fuer diesen Fall.
function scheduleInitialDrawAutosave() {
  syncTemporaryOrderHint();
  if (!(Number(S.project?.persistence?.auftragId || 0) > 0)) {
    autosaveCurrentState();
    return;
  }
  if (initialDrawAutosaveTimer) return;
  initialDrawAutosaveTimer = window.setTimeout(() => {
    initialDrawAutosaveTimer = 0;
    autosaveCurrentState();
  }, CHANGE_AUTOSAVE_DEBOUNCE_MS);
}

function collectAppState() {
  syncProjectItems();
  ensureDefaultProjectContext();
  const drawingSheets = serializeDrawingSheets();
  const document = createProjectDocument({
    project: S.project,
    camera2D: S.camera2D,
    activeCompanyId: SystemRegistry.state?.activeCompanyId || null,
    activeCategoryId: SystemRegistry.state?.activeCategoryId || null,
    activeSystemId: SystemRegistry.state?.activeSystemId || null,
    savedAt: new Date().toISOString()
  });
  // Der Serializer liefert bereits eine abgetrennte JSON-Kopie aller Ebenen.
  document.drawingSheets = drawingSheets;
  return finalizeProjectDocument(document);
}

function userDisplayName(user) {
  return normalizeText(user?.display_name || `${user?.vorname || ''} ${user?.nachname || ''}`);
}

function userMatchesInput(user, input) {
  const needle = normalizeComparable(input);
  if (!needle) return false;
  return [
    userDisplayName(user),
    user?.login_name,
    user?.email
  ].some(value => normalizeComparable(value) === needle);
}

// Verkauf/AVOR: nur Personen, die als interne Benutzer erfasst sind.
async function searchUsers(query, options = {}) {
  const payload = await fetch(apiUrl('/api/users/search', {
    q: query,
    role: options.roleCode || options.role || '',
    internal: options.internal ? '1' : ''
  })).then(readJsonResponse);
  return Array.isArray(payload.users) ? payload.users : [];
}

async function resolveProjectUserField(config, saveType) {
  const { textField, idField, roleCode, fieldLabel } = config;
  const name = normalizeText(S.project?.[textField]);
  if (!name) {
    S.project[idField] = null;
    return;
  }
  if (normalizeComparable(name) === normalizeComparable(DEFAULT_TEMP_USER_LABEL)) {
    S.project[idField] = null;
    return;
  }

  const users = await searchUsers(name, { roleCode, internal: true });
  const exact = users.find(user => userMatchesInput(user, name));
  if (exact) {
    S.project[textField] = userDisplayName(exact);
    S.project[idField] = exact.benutzer_id ?? null;
    return;
  }

  S.project[idField] = null;
  if (saveType === 'autosave') {
    updateAutosaveStatus({
      state: 'dirty',
      label: `${fieldLabel} noch nicht erfasst`,
      error: `${fieldLabel} "${name}" ist noch kein Benutzer. Autosave speichert den Namen als Text.`
    });
    return;
  }

  const createdUser = await openUserQuickCreateModal({ fieldLabel, roleCode, name });
  S.project[textField] = userDisplayName(createdUser);
  S.project[idField] = createdUser?.benutzer_id ?? null;
}

async function ensureProjectUserAssignments(saveType) {
  ensureDefaultProjectContext();
  await resolveProjectUserField({
    textField: 'seller',
    idField: 'sellerUserId',
    roleCode: 'VERKAUF',
    fieldLabel: 'Verkäufer'
  }, saveType);
  await resolveProjectUserField({
    textField: 'avor',
    idField: 'avorUserId',
    roleCode: 'AVOR',
    fieldLabel: 'AVOR'
  }, saveType);
}

async function restoreAppState(state) {
  const parsedPayload = parseProjectDocument(state);
  const project = parsedPayload?.project;
  if (!project || typeof project !== 'object') {
    throw new Error('Gespeicherter Auftrag enthält keinen gültigen Projektzustand.');
  }

  await ensureColorCatalogsForProject(project);
  await SystemRegistry.ensureSystemsForProject(project, {
    activeSystemId: parsedPayload.activeSystemId
  });

  // Erst nachdem alle benötigten Kataloge und Systeme erfolgreich geladen sind,
  // wird der laufende Zeichnungsebenen-Zustand ersetzt. So bleibt der bisherige
  // Auftrag bei einem Lazy-Load-Fehler vollständig aktiv. Produktwerkzeuge und
  // Auswahlen werden dabei noch gegen den alten Projektzustand beendet.
  recordUndo('Auftrag laden');
  resetTransientProjectRuntimeState();
  const payload = restoreDrawingSheetsFromDocument(parsedPayload);
  S.project = payload.project;
  if (payload.camera2D) S.camera2D = cloneDeep(payload.camera2D);

  if (payload.activeCompanyId && SystemRegistry.getCompany(payload.activeCompanyId)) {
    SystemRegistry.state.activeCompanyId = payload.activeCompanyId;
  }
  if (payload.activeCategoryId && SystemRegistry.getCategory(payload.activeCategoryId)) {
    const category = SystemRegistry.getCategory(payload.activeCategoryId);
    SystemRegistry.state.activeCategoryId = category.id;
    if (category.companyId) SystemRegistry.state.activeCompanyId = category.companyId;
  }
  if (payload.activeSystemId && SystemRegistry.getSystem(payload.activeSystemId)) {
    const system = SystemRegistry.getSystem(payload.activeSystemId);
    SystemRegistry.state.activeSystemId = system.id;
    if (system.categoryId) SystemRegistry.state.activeCategoryId = system.categoryId;
    if (system.companyId) SystemRegistry.state.activeCompanyId = system.companyId;
  }

  ensureDefaultProjectContext();
  AppOptionsController.markProjectGridOptionsTouched?.();
  normalizeProjectState({ mode: 'hydrate', source: 'order-state-load' });
  SystemRegistry.normalizeNavigationState();
  SystemRegistry.renderShell();
  AppRuntime.refresh(true);
  if (!S.file) S.file = { name: '', dirty: false };
  S.file.dirty = false;
  syncTemporaryOrderHint();
}

async function performSaveCurrentState(options = {}) {
  const saveType = options.saveType === 'autosave' ? 'autosave' : 'manual';
  ensureDefaultProjectContext();
  const maySave = await OrderEditingSession.ensureOrderMaySave({
    showDialog: saveType === 'manual'
  });
  if (!maySave) {
    const error = new Error(OrderEditingSession.orderEditingState().status === 'stale'
      ? 'Neuere Zeichnungsversion verfügbar. Speichern nicht möglich.'
      : 'Speichern nicht möglich, da ein anderer Benutzer den Auftrag bearbeitet.');
    error.orderEditingConflict = true;
    throw error;
  }
  const draftOrderNumber = (saveType === 'manual' || options.maintenance === true) && options.useInputDrafts !== false
    ? S.project?.inputDrafts?.orderNumber : undefined;
  if (options.requireContent !== false && !hasSavableProjectContent(options.orderNumber ?? draftOrderNumber ?? S.project?.orderNumber)) {
    throw new Error('Leere Zeichnungen werden nicht gespeichert.');
  }
  if (Object.prototype.hasOwnProperty.call(options, 'seller')) {
    S.project.seller = normalizeText(options.seller);
    S.project.sellerUserId = options.sellerUserId ?? null;
  }
  if (Object.prototype.hasOwnProperty.call(options, 'avor')) {
    S.project.avor = normalizeText(options.avor);
    S.project.avorUserId = options.avorUserId ?? null;
  }
  // Race-Fix (Nutzer-Meldung 2026-08-18: Groessenaenderungen an Objekten
  // gingen nach Autosave manchmal verloren): S.file.dirty wurde bisher am
  // Ende IMMER auf false gesetzt, sobald dieser Speichervorgang erfolgreich
  // war -- auch wenn WAEHREND des laufenden (asynchronen) Speicherns
  // bereits eine NEUERE Aenderung passiert ist. recordUndo() erhoehte dabei
  // zwar S.file.dirty auf true, aber der spaeter abschliessende, aeltere
  // Speichervorgang ueberschrieb das wieder mit false -- danach wartete kein
  // Timer mehr auf einen erneuten Autosave, die neuere Aenderung blieb
  // unsaved. Fix: die Modell-Revision beim Start dieses Speichervorgangs
  // merken und dirty nur zuruecksetzen, wenn sich seither nichts mehr
  // geaendert hat.
  const projectModelRevisionAtSaveStart = getProjectModelRevision();
  await ensureProjectUserAssignments(saveType);
  const inputDrafts = (saveType === 'manual' || options.maintenance === true) && options.useInputDrafts !== false && S.project?.inputDrafts
    ? S.project.inputDrafts
    : {};
  const hasDraftValue = field => Object.prototype.hasOwnProperty.call(inputDrafts, field);
  const projectNumber = normalizeText(options.projectNumber ?? (hasDraftValue('projectNumber') ? inputDrafts.projectNumber : S.project.projectNumber)) || DEFAULT_PROJECT_NUMBER;
  const orderNumber = normalizeText(options.orderNumber ?? (hasDraftValue('orderNumber') ? inputDrafts.orderNumber : S.project.orderNumber));
  const orderDescription = normalizeText(options.orderDescription ?? (hasDraftValue('orderDescription') ? inputDrafts.orderDescription : S.project.orderDescription));
  const orderNumberIsTemp = isTempOrderNumber(orderNumber);
  const isTemp = saveType === 'autosave'
    ? (options.isTemp === true || !orderNumber || orderNumberIsTemp)
    : !orderNumber;
  const hasExplicitCustomerName = Object.prototype.hasOwnProperty.call(options, 'customerName') || hasDraftValue('customerName');
  const selectedCustomerName = normalizeText(
    options.customerName ?? (hasDraftValue('customerName') ? inputDrafts.customerName : S.project.customerName)
  );
  const suppressTempCustomerMetadata = isTemp && saveType === 'autosave' && !hasExplicitCustomerName;
  const customerName = suppressTempCustomerMetadata ? '' : selectedCustomerName;
  const customerIdentity = customerIdentityForOptions(selectedCustomerName, options);
  const customerContactIdentity = customerContactIdentityForOptions(customerIdentity.customerId, options);
  const state = collectAppState();
  state.project = {
    ...(state.project || {}),
    projectNumber,
    customerName: selectedCustomerName,
    ...customerIdentity,
    ...customerContactIdentity,
    orderNumber,
    orderDescription
  };
  const orderNumberForSave = saveType === 'manual' && orderNumberIsTemp ? '' : orderNumber;
  const previousTempOrderId = S.project.persistence?.isTemp ? S.project.persistence.auftragId : null;

  updateAutosaveStatus({
    state: 'saving',
    label: saveType === 'autosave' ? 'Autosave läuft…' : 'Speichern läuft…',
    error: ''
  });

  let payload;
  try {
    const statisticsSnapshot = saveType === 'manual' && !isTemp
      ? await collectManualStatisticsSnapshot(state.project)
      : null;
    payload = await releaseClientFetch(apiUrl(saveType === 'autosave' ? '/api/orders/autosave' : '/api/orders/save'), {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        projectNumber,
        customerName,
        ...(suppressTempCustomerMetadata ? {} : customerIdRequestFields(customerIdentity, options)),
        ...(suppressTempCustomerMetadata ? {} : customerContactRequestFields(customerContactIdentity, options)),
        orderNumber: orderNumberForSave,
        orderDescription,
        seller: S.project.seller || '',
        avor: S.project.avor || '',
        sellerUserId: S.project.sellerUserId ?? null,
        avorUserId: S.project.avorUserId ?? null,
        previousTempOrderId,
        isTemp,
        state,
        ...(statisticsSnapshot ? { statisticsSnapshot } : {}),
        ...OrderEditingSession.editingSessionRequestFields()
      })
    }).then(readJsonResponse);
  } catch (error) {
    if (await OrderEditingSession.handleOrderSaveConflict(error, { showDialog: saveType === 'manual' })) {
      error.orderEditingConflict = true;
    }
    throw error;
  }

  await OrderEditingSession.applySaveSuccess(payload);

  const order = payload.order || {};
  S.project.projectNumber = normalizeText(order.projektnummer || projectNumber) || DEFAULT_PROJECT_NUMBER;
  applyOrderCustomerIdentity(suppressTempCustomerMetadata ? {
    customerName: selectedCustomerName,
    customerId: customerIdentity.customerId,
    ...customerContactIdentity
  } : { ...customerContactIdentity, ...order }, selectedCustomerName);
  S.project.orderNumber = normalizeText(order.auftragsnummer || orderNumber);
  S.project.orderDescription = normalizeText(order.bezeichnung ?? orderDescription);
  if (S.project.inputDrafts && typeof S.project.inputDrafts === 'object') {
    S.project.inputDrafts = {};
  }
  S.project.sellerUserId = order.verkaeufer_benutzer_id ?? S.project.sellerUserId ?? null;
  S.project.avorUserId = order.avor_benutzer_id ?? S.project.avorUserId ?? null;
  S.project.persistence = {
    ...(S.project.persistence || {}),
    auftragId: order.auftrag_id ?? null,
    projektId: order.projekt_id ?? S.project.persistence?.projektId ?? null,
    stateId: payload.state_id ?? null,
    versionNr: payload.version_nr ?? null,
    isTemp: order.ist_temp === true,
    lastSavedAt: order.geaendert_am || new Date().toISOString()
  };
  if (!S.file) S.file = { name: '', dirty: false };
  // Nur als "gespeichert" markieren, wenn seit Beginn dieses (asynchronen)
  // Speichervorgangs keine neuere Aenderung mehr passiert ist -- siehe
  // Kommentar bei projectModelRevisionAtSaveStart oben. Sonst bleibt dirty
  // stehen, damit der naechste Autosave-Timer die neuere Aenderung noch
  // erfasst statt sie stillschweigend zu verlieren.
  if (getProjectModelRevision() === projectModelRevisionAtSaveStart) {
    S.file.dirty = false;
  }
  syncTemporaryOrderHint();
  SystemRegistry.renderShell();
  AppRuntime.refresh(false);
  updateAutosaveStatus({
    state: 'saved',
    label: `Gespeichert ${formatSavedTime(S.project.persistence.lastSavedAt)}`,
    lastSavedAt: S.project.persistence.lastSavedAt,
    error: ''
  });
  const statisticsWarning = normalizeText(payload.statisticsWarning);
  if (statisticsWarning) console.warn(statisticsWarning);
  setStatus(statisticsWarning
    ? `Auftrag gespeichert. Statistik: ${statisticsWarning}`
    : (saveType === 'autosave' ? 'Autosave gespeichert.' : 'Auftrag gespeichert.'));
  rememberLastOpenedOrder(payload.order || {});
  return payload;
}

async function saveCurrentState(options = {}) {
  const saveType = options.saveType === 'autosave' ? 'autosave' : 'manual';
  if (saveRequestInFlight) {
    if (saveType === 'autosave') return null;
    throw new Error('Ein Speichervorgang läuft bereits.');
  }
  saveRequestInFlight = true;
  try {
    return await performSaveCurrentState(options);
  } finally {
    saveRequestInFlight = false;
  }
}

// Unlike autosave, this must propagate every failure to the release barrier.
async function flushForMaintenance({ inputDrafts = {} } = {}) {
  const capturedDrafts = { ...(S.project?.inputDrafts || {}), ...inputDrafts };
  const deadline = Date.now() + 45000;
  const saving = () => saveRequestInFlight || autosaveInFlight;
  while (saving()) {
    if (Date.now() > deadline) throw new Error('Laufender Speichervorgang wird nicht fertig.');
    await new Promise(resolve => { setTimeout(resolve, 100); });
  }
  if (Object.keys(capturedDrafts).length) S.project.inputDrafts = { ...capturedDrafts, ...(S.project.inputDrafts || {}) };
  if (!S.file?.dirty && !Object.keys(S.project?.inputDrafts || {}).length) return;
  if (!hasSavableProjectContent(S.project?.inputDrafts?.orderNumber ?? S.project?.orderNumber) && !S.project?.persistence?.auftragId) {
    // Empty initial workspace has no order to persist.
    S.file.dirty = false;
    return;
  }
  const orderNumber = normalizeText(S.project?.inputDrafts?.orderNumber ?? S.project?.orderNumber);
  const payload = await saveCurrentState({ saveType: 'autosave', requireContent: false, maintenance: true,
    useInputDrafts: true, isTemp: !orderNumber || isTempOrderNumber(orderNumber) });
  if (!payload || S.file?.dirty) throw new Error('Zeichnung wurde während der Sicherung geändert. Erneute Speicherung erforderlich.');
}

async function ensureCurrentOrderSaved(options = {}) {
  ensureDefaultProjectContext();
  const orderNumber = normalizeText(S.project?.orderNumber);
  if (options.requireFinalMeta === true && (!orderNumber || isTempOrderNumber(orderNumber))) {
    throw new Error('Bitte im Dossier zuerst die richtige Projektnummer und Auftragsnummer übernehmen.');
  }
  const currentOrderId = Number(S.project?.persistence?.auftragId || 0);
  if (Number.isFinite(currentOrderId) && currentOrderId > 0) return currentOrderId;
  if (options.requireContent !== false && !hasSavableProjectContent()) {
    throw new Error('Leere Zeichnungen werden nicht gespeichert.');
  }
  const payload = await saveCurrentState({
    saveType: options.saveType || 'manual',
    isTemp: !orderNumber || isTempOrderNumber(orderNumber)
  });
  const orderId = Number(payload?.order?.auftrag_id || S.project?.persistence?.auftragId || 0);
  if (!Number.isFinite(orderId) || orderId <= 0) {
    throw new Error('Aktueller Auftrag konnte nicht gespeichert werden.');
  }
  return orderId;
}

async function autosaveCurrentState() {
  if (OrderEditingSession.isOrderSaveBlocked()) return null;
  if (!S.file?.dirty) {
    refreshDirtyStatus();
    return null;
  }
  if (!hasSavableProjectContent()) {
    refreshDirtyStatus();
    return null;
  }
  if (autosaveInFlight) return null;
  autosaveInFlight = true;
  try {
    const orderNumber = normalizeText(S.project?.orderNumber);
    return await saveCurrentState({ saveType: 'autosave', isTemp: !orderNumber || isTempOrderNumber(orderNumber) });
  } catch (error) {
    if (error?.orderEditingConflict) return null;
    updateAutosaveStatus({ state: 'error', label: 'Autosave fehlgeschlagen', error: error.message || String(error) });
    setStatus(`Autosave fehlgeschlagen: ${error.message || error}`);
    return null;
  } finally {
    autosaveInFlight = false;
  }
}

async function manualSaveCurrentState(options = {}) {
  try {
    return await saveCurrentState({
      saveType: 'manual',
      isTemp: !normalizeText(S.project?.orderNumber),
      useInputDrafts: options.useInputDrafts !== false
    });
  } catch (error) {
    if (error?.orderEditingConflict) {
      setStatus(error.message || 'Speichern nicht möglich.');
      return null;
    }
    updateAutosaveStatus({ state: 'error', label: 'Speichern fehlgeschlagen', error: error.message || String(error) });
    setStatus(`Speichern fehlgeschlagen: ${error.message || error}`);
    return null;
  }
}

async function saveAsCurrentState(options = {}) {
  ensureDefaultProjectContext();
  if (options.requireContent !== false && !hasSavableProjectContent(options.orderNumber)) {
    throw new Error('Leere Zeichnungen werden nicht gespeichert.');
  }
  const projectNumber = normalizeText(options.projectNumber);
  const customerName = normalizeText(options.customerName);
  const customerIdentity = customerIdentityForOptions(customerName, options);
  const customerContactIdentity = customerContactIdentityForOptions(customerIdentity.customerId, options);
  const orderNumber = normalizeText(options.orderNumber);
  const orderDescription = normalizeText(options.orderDescription);
  const seller = normalizeText(options.seller);
  const avor = normalizeText(options.avor);
  const sellerUserId = options.sellerUserId ?? null;
  const avorUserId = options.avorUserId ?? null;
  if (!projectNumber) throw new Error('Projektnummer ist erforderlich.');
  if (!orderNumber) throw new Error('Auftragsnummer ist erforderlich.');
  if (!seller || !sellerUserId) throw new Error('Verkäufer ist erforderlich.');

  const state = collectAppState();
  state.project = {
    ...(state.project || {}),
    projectNumber,
    customerName,
    ...customerIdentity,
    ...customerContactIdentity,
    orderNumber,
    orderDescription,
    seller,
    avor,
    sellerUserId,
    avorUserId,
    persistence: {}
  };

  updateAutosaveStatus({ state: 'saving', label: 'Speichern unter läuft…', error: '' });
  const statisticsSnapshot = await collectManualStatisticsSnapshot(state.project);
  const payload = await releaseClientFetch(apiUrl('/api/orders/save-as'), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      sourceAuftragId: S.project.persistence?.auftragId ?? null,
      projectNumber,
      customerName,
      ...customerIdRequestFields(customerIdentity, options),
      ...customerContactRequestFields(customerContactIdentity, options),
      orderNumber,
      orderDescription,
      seller,
      avor,
      sellerUserId,
      avorUserId,
      stateJson: state,
      ...(statisticsSnapshot ? { statisticsSnapshot } : {}),
      ...OrderEditingSession.editingSessionRequestFields()
    })
  }).then(readJsonResponse);

  await OrderEditingSession.applySaveSuccess(payload);

  const order = { ...customerContactIdentity, ...(payload.order || {}) };
  S.project.projectNumber = normalizeText(order.projektnummer || projectNumber);
  applyOrderCustomerIdentity(order, customerName);
  S.project.orderNumber = normalizeText(order.auftragsnummer || orderNumber);
  S.project.orderDescription = normalizeText(order.bezeichnung ?? orderDescription);
  S.project.seller = seller;
  S.project.avor = avor;
  S.project.sellerUserId = order.verkaeufer_benutzer_id ?? sellerUserId;
  S.project.avorUserId = order.avor_benutzer_id ?? avorUserId;
  S.project.persistence = {
    ...(S.project.persistence || {}),
    auftragId: order.auftrag_id ?? null,
    projektId: order.projekt_id ?? S.project.persistence?.projektId ?? null,
    stateId: payload.state_id ?? null,
    versionNr: payload.version_nr ?? null,
    isTemp: false,
    lastSavedAt: order.geaendert_am || new Date().toISOString()
  };
  if (!S.file) S.file = { name: '', dirty: false };
  S.file.dirty = false;
  syncTemporaryOrderHint();
  SystemRegistry.renderShell();
  AppRuntime.refresh(false);
  updateAutosaveStatus({
    state: 'saved',
    label: `Gespeichert ${formatSavedTime(S.project.persistence.lastSavedAt)}`,
    lastSavedAt: S.project.persistence.lastSavedAt,
    error: ''
  });
  const statisticsWarning = normalizeText(payload.statisticsWarning);
  if (statisticsWarning) console.warn(statisticsWarning);
  setStatus(statisticsWarning
    ? `Auftrag ${S.project.orderNumber} gespeichert. Statistik: ${statisticsWarning}`
    : `Auftrag ${S.project.orderNumber} als Kopie gespeichert.`);
  rememberLastOpenedOrder(payload.order || {});
  return payload;
}

async function searchProjects(query) {
  const payload = await fetch(apiUrl('/api/projects/search', { q: query })).then(readJsonResponse);
  return Array.isArray(payload.projects) ? payload.projects : [];
}

async function searchCustomers(query, options = {}) {
  const payload = await fetch(apiUrl('/api/customers/search', {
    q: query,
    limit: options.limit || ''
  })).then(readJsonResponse);
  return Array.isArray(payload.customers) ? payload.customers : [];
}

async function searchCustomerContacts(customerId, query = '', options = {}) {
  const numericCustomerId = positiveCustomerId(customerId);
  if (!numericCustomerId) return [];
  const payload = await fetch(apiUrl(`/api/customers/${numericCustomerId}/contacts`, {
    q: query,
    limit: options.limit || ''
  })).then(readJsonResponse);
  return Array.isArray(payload.contacts) ? payload.contacts : [];
}

// Personenstamm für interne Benutzer, z. B. um eine bereits erfasste Person
// als Ansprechperson einer weiteren Firma zu übernehmen.
async function searchPersons(query, options = {}) {
  const payload = await fetch(apiUrl('/api/customers/persons/search', {
    q: query,
    users: options.users || '',
    limit: options.limit || ''
  })).then(readJsonResponse);
  return Array.isArray(payload.persons) ? payload.persons : [];
}

async function createCustomer(customer = {}) {
  const payload = await fetch(apiUrl('/api/customers'), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(customer)
  }).then(readJsonResponse);
  return payload.customer || payload.kunde || payload;
}

async function createCustomerContact(customerId, contact = {}) {
  const numericCustomerId = positiveCustomerId(customerId);
  if (!numericCustomerId) throw new Error('Bitte zuerst eine Firma aus dem Kundenstamm auswählen.');
  const payload = await fetch(apiUrl(`/api/customers/${numericCustomerId}/contacts`), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(contact)
  }).then(readJsonResponse);
  return payload.contact || payload.kontakt || payload;
}

async function getCustomerContactMergePreview(customerId, sourceContactId, targetContactId) {
  const numericCustomerId = positiveCustomerId(customerId);
  const numericSourceId = positiveCustomerId(sourceContactId);
  const numericTargetId = positiveCustomerId(targetContactId);
  if (!numericCustomerId || !numericSourceId || !numericTargetId) {
    throw new Error('Kontakt-Zusammenführung ist unvollständig.');
  }
  const payload = await fetch(apiUrl(
    `/api/admin/customers/${numericCustomerId}/contacts/${numericSourceId}/merge-preview`,
    { targetContactId: numericTargetId }
  )).then(readJsonResponse);
  return payload.mergePreview || payload.preview || payload;
}

function mergeCustomerContact(customerId, sourceContactId, targetContactId) {
  const numericCustomerId = positiveCustomerId(customerId);
  const numericSourceId = positiveCustomerId(sourceContactId);
  const numericTargetId = positiveCustomerId(targetContactId);
  if (!numericCustomerId || !numericSourceId || !numericTargetId) {
    throw new Error('Kontakt-Zusammenführung ist unvollständig.');
  }
  return fetch(apiUrl(
    `/api/admin/customers/${numericCustomerId}/contacts/${numericSourceId}/merge`
  ), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ targetContactId: numericTargetId })
  }).then(readJsonResponse);
}

async function searchOrders(query, options = {}) {
  const payload = await fetch(apiUrl('/api/orders/search', {
    q: query,
    projektnummer: options.projectNumber || ''
  })).then(readJsonResponse);
  return Array.isArray(payload.orders) ? payload.orders : [];
}

function resolveOrderNumber(projectNumber, orderNumber) {
  return fetch(apiUrl('/api/orders/resolve', {
    projektnummer: projectNumber || '',
    auftragsnummer: orderNumber || ''
  })).then(readJsonResponse);
}

async function loadOrdersForProject(projectNumber) {
  const normalized = normalizeText(projectNumber);
  if (!normalized) return [];
  const payload = await fetch(apiUrl(`/api/projects/${encodeURIComponent(normalized)}/orders`)).then(readJsonResponse);
  return Array.isArray(payload.orders) ? payload.orders : [];
}

async function loadOrderByProjectOrder(projectNumber, orderNumber) {
  const normalizedProject = normalizeText(projectNumber) || DEFAULT_PROJECT_NUMBER;
  const normalizedOrder = normalizeText(orderNumber);
  if (!normalizedOrder) throw new Error('Auftragsnummer fehlt.');

  updateAutosaveStatus({ state: 'loading', label: 'Auftrag wird geladen…', error: '' });
  const payload = await fetch(apiUrl('/api/orders/by-project-order', {
    projektnummer: normalizedProject,
    auftragsnummer: normalizedOrder,
    ...OrderEditingSession.editingSessionLoadQuery()
  })).then(readJsonResponse);
  await restoreAppState(payload.state);
  S.project.projectNumber = payload.order?.projektnummer || normalizedProject;
  applyOrderCustomerIdentity(payload.order || {}, S.project.customerName);
  S.project.orderNumber = payload.order?.auftragsnummer || normalizedOrder;
  S.project.orderDescription = normalizeText(payload.order?.bezeichnung ?? S.project.orderDescription);
  S.project.seller = normalizeText(payload.order?.verkaeufer_name ?? '');
  S.project.avor = normalizeText(payload.order?.avor_name ?? '');
  if (S.project.inputDrafts && typeof S.project.inputDrafts === 'object') {
    S.project.inputDrafts = {};
  }
  S.project.sellerUserId = payload.order?.verkaeufer_benutzer_id ?? S.project.sellerUserId ?? null;
  S.project.avorUserId = payload.order?.avor_benutzer_id ?? S.project.avorUserId ?? null;
  S.project.persistence = {
    ...(S.project.persistence || {}),
    auftragId: payload.order?.auftrag_id ?? null,
    projektId: payload.order?.projekt_id ?? S.project.persistence?.projektId ?? null,
    stateId: payload.state_id ?? null,
    versionNr: payload.versionNr ?? payload.version_nr ?? null,
    isTemp: payload.order?.ist_temp === true,
    lastSavedAt: payload.order?.geaendert_am || new Date().toISOString()
  };
  if (!S.file) S.file = { name: '', dirty: false };
  S.file.dirty = false;
  syncTemporaryOrderHint();
  SystemRegistry.renderShell();
  AppRuntime.refresh(true);
  updateAutosaveStatus({
    state: 'saved',
    label: `Geladen ${formatSavedTime(S.project.persistence.lastSavedAt)}`,
    lastSavedAt: S.project.persistence.lastSavedAt,
    error: ''
  });
  setStatus(`Auftrag ${S.project.orderNumber} geladen.`);
  rememberLastOpenedOrder(payload.order || {});
  await OrderEditingSession.activateOrderEditingSession({
    orderId: payload.order?.auftrag_id,
    baseVersionNr: payload.versionNr ?? payload.version_nr,
    editingSession: payload.editingSession,
    announceBlocked: true
  });
  return payload;
}

async function loadOrderWithUnsavedGuard(projectNumber, orderNumber) {
  const normalizedProject = normalizeText(projectNumber) || DEFAULT_PROJECT_NUMBER;
  const normalizedOrder = normalizeText(orderNumber);
  const current = currentOrderMeta();
  if (
    normalizeComparable(current.projektnummer) === normalizeComparable(normalizedProject)
    && normalizeComparable(current.auftragsnummer) === normalizeComparable(normalizedOrder)
  ) return { alreadyOpen: true };
  const unsavedAction = await confirmUnsavedDrawingBeforeReset();
  if (unsavedAction === 'cancel') return null;
  if (unsavedAction === 'save') {
    const saved = await manualSaveCurrentState();
    if (!saved && S.file?.dirty) throw new Error('Aktuelle Zeichnung konnte vor dem Wechsel nicht gespeichert werden.');
  }
  return loadOrderByProjectOrder(normalizedProject, normalizedOrder);
}

async function autoOpenLastOrderIfEnabled(options = {}) {
  if (autoOpenLastOrderAttempted) return null;
  if (options.enabled !== true) return null;
  autoOpenLastOrderAttempted = true;
  const lastOrder = readLastOpenedOrder();
  if (!lastOrder) return null;
  if (
    normalizeComparable(lastOrder.projectNumber) === normalizeComparable(S.project?.projectNumber)
    && normalizeComparable(lastOrder.orderNumber) === normalizeComparable(S.project?.orderNumber)
    && hasProjectDrawingContent()
  ) {
    return null;
  }
  if (S.file?.dirty && hasSavableProjectContent()) return null;

  try {
    setStatus(`Letzter Auftrag wird geladen: ${lastOrder.projectNumber} / ${lastOrder.orderNumber}`);
    return await withWorkspaceLoadingOverlay(
      () => loadOrderByProjectOrder(lastOrder.projectNumber, lastOrder.orderNumber),
      {
        label: 'Letzter Auftrag wird geladen...',
        details: `Projekt ${lastOrder.projectNumber} · Auftrag ${lastOrder.orderNumber}`
      }
    );
  } catch (error) {
    console.warn('Letzter Auftrag konnte nicht automatisch geladen werden', error);
    setStatus(`Letzter Auftrag konnte nicht geladen werden: ${error.message || error}`);
    return null;
  }
}

async function startNewDetachedProject() {
  if (newDetachedProjectInFlight) return false;
  newDetachedProjectInFlight = true;
  try {
    const unsavedAction = await confirmUnsavedDrawingBeforeReset();
    if (unsavedAction === 'cancel') {
      setStatus('Neuer Auftrag abgebrochen.');
      return false;
    }
    if (unsavedAction === 'save') {
      const saved = await manualSaveCurrentState();
      if (!saved && S.file?.dirty) {
        setStatus('Neuer Auftrag wurde nicht geöffnet: aktuelle Zeichnung konnte nicht gespeichert werden.');
        return false;
      }
    }

    await OrderEditingSession.leaveCurrentOrder();

    resetTransientProjectRuntimeState();
    S.project = blankDetachedProjectState();
    resetDrawingSheets();
    S.camera2D = { x: 0, z: 0, scale: 0.25 };
    if (!S.history || typeof S.history !== 'object') S.history = { undo: [], redo: [], limit: 80 };
    S.history.undo = [];
    S.history.redo = [];

    normalizeProjectState({ mode: 'create', source: 'new-detached-project' });
    SystemRegistry.normalizeNavigationState();
    SystemRegistry.renderShell();
    AppRuntime.refresh(true);
    if (!S.file) S.file = { name: '', dirty: false };
    S.file.name = '';
    S.file.dirty = false;
    syncTemporaryOrderHint();
    updateAutosaveStatus({
      state: 'saved',
      label: 'Neuer Auftrag',
      lastSavedAt: null,
      error: ''
    });
    setStatus('Neuer Auftrag geöffnet.');
    return true;
  } finally {
    newDetachedProjectInFlight = false;
  }
}

function currentOrderMeta() {
  ensureDefaultProjectContext();
  return {
    auftragId: S.project.persistence?.auftragId ?? null,
    projektId: S.project.persistence?.projektId ?? null,
    projektnummer: S.project.projectNumber || DEFAULT_PROJECT_NUMBER,
    kunde: S.project.customerName || '',
    kunde_id: S.project.customerId ?? null,
    customerId: S.project.customerId ?? null,
    kunde_kontakt_id: S.project.customerContactId ?? null,
    customerContactId: S.project.customerContactId ?? null,
    customerContactFirstName: S.project.customerContactFirstName || '',
    customerContactLastName: S.project.customerContactLastName || '',
    customerContactName: normalizedPersonName(
      S.project.customerContactFirstName,
      S.project.customerContactLastName,
      S.project.customerContactName
    ),
    auftragsnummer: S.project.orderNumber || '',
    bezeichnung: S.project.orderDescription || '',
    verkaeufer: S.project.seller || '',
    avor: S.project.avor || '',
    sellerUserId: S.project.sellerUserId ?? null,
    avorUserId: S.project.avorUserId ?? null,
    isTemp: S.project.persistence?.isTemp === true,
    geaendertAm: S.project.persistence?.lastSavedAt || ''
  };
}

async function loadOrderDossier(orderId, options = {}) {
  const numericOrderId = Number(orderId || S.project?.persistence?.auftragId || 0);
  if (!Number.isFinite(numericOrderId) || numericOrderId <= 0) return { order: null, files: [] };
  const payload = await fetch(apiUrl(`/api/orders/${numericOrderId}/dossier`, {
    type: options.type || ''
  })).then(readJsonResponse);
  return {
    order: payload.order || null,
    files: Array.isArray(payload.files) ? payload.files : []
  };
}

function getOrderDeletePreview(orderId) {
  const numericOrderId = Number(orderId || S.project?.persistence?.auftragId || 0);
  if (!Number.isFinite(numericOrderId) || numericOrderId <= 0) {
    throw new Error('Auftrag muss zuerst gespeichert sein.');
  }
  return fetch(apiUrl(`/api/orders/${numericOrderId}/delete-preview`)).then(readJsonResponse);
}

function resetCurrentProjectAfterDelete() {
  recordUndo('Auftrag löschen');
  resetTransientProjectRuntimeState();
  S.project = {
    projectNumber: DEFAULT_PROJECT_NUMBER,
    customerName: '',
    customerId: null,
    customerIdentityName: '',
    customerContactId: null,
    customerContactFirstName: '',
    customerContactLastName: '',
    customerContactName: '',
    orderNumber: '',
    orderDescription: '',
    seller: '',
    avor: '',
    sellerUserId: null,
    avorUserId: null,
    persistence: {}
  };
  resetDrawingSheets();
  normalizeProjectState({ mode: 'create', source: 'order-delete-reset' });
  SystemRegistry.renderShell();
  AppRuntime.refresh(true);
  if (!S.file) S.file = { name: '', dirty: false };
  S.file.dirty = false;
  syncTemporaryOrderHint();
  updateAutosaveStatus({
    state: 'saved',
    label: 'Auftrag gelöscht',
    lastSavedAt: null,
    error: ''
  });
}

async function deleteCurrentOrder(options = {}) {
  const numericOrderId = Number(options.orderId || S.project?.persistence?.auftragId || 0);
  if (!Number.isFinite(numericOrderId) || numericOrderId <= 0) {
    throw new Error('Auftrag muss zuerst gespeichert sein.');
  }
  if (!(await OrderEditingSession.ensureOrderMaySave({ showDialog: true }))) {
    throw new Error('Löschen nicht möglich, da ein anderer Benutzer den Auftrag bearbeitet.');
  }
  updateAutosaveStatus({ state: 'saving', label: 'Auftrag wird gelöscht…', error: '' });
  let payload;
  try {
    payload = await fetch(apiUrl(`/api/orders/${numericOrderId}`), {
      method: 'DELETE',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        deleteProject: options.deleteProject === true,
        ...OrderEditingSession.editingSessionRequestFields()
      })
    }).then(readJsonResponse);
  } catch (error) {
    await OrderEditingSession.handleOrderSaveConflict(error, { showDialog: true });
    throw error;
  }
  if (Number(S.project?.persistence?.auftragId || 0) === numericOrderId) {
    await OrderEditingSession.leaveCurrentOrder();
    resetCurrentProjectAfterDelete();
  }
  setStatus(options.deleteProject === true && payload.deletedProjectId
    ? 'Auftrag und Projekt gelöscht.'
    : 'Auftrag gelöscht.');
  return payload;
}

async function updateCurrentOrderMeta(options = {}) {
  const numericOrderId = Number(options.orderId || S.project?.persistence?.auftragId || 0);
  if (!Number.isFinite(numericOrderId) || numericOrderId <= 0) {
    throw new Error('Auftrag muss zuerst gespeichert sein.');
  }
  if (!(await OrderEditingSession.ensureOrderMaySave({ showDialog: true }))) {
    const error = new Error('Speichern nicht möglich, da ein anderer Benutzer den Auftrag bearbeitet.');
    error.orderEditingConflict = true;
    throw error;
  }
  const projectNumber = normalizeText(options.projectNumber) || DEFAULT_PROJECT_NUMBER;
  const customerName = normalizeText(options.customerName);
  const customerIdentity = customerIdentityForOptions(customerName, options);
  const customerContactIdentity = customerContactIdentityForOptions(customerIdentity.customerId, options);
  const orderNumber = normalizeText(options.orderNumber);
  const orderDescription = normalizeText(options.orderDescription);
  if (!orderNumber) throw new Error('Auftragsnummer ist erforderlich.');
  const seller = normalizeText(options.seller);
  const avor = normalizeText(options.avor);
  ensureDefaultProjectContext();
  const previousMeta = {
    customerName: S.project.customerName,
    seller: S.project.seller,
    avor: S.project.avor,
    sellerUserId: S.project.sellerUserId,
    avorUserId: S.project.avorUserId
  };
  let resolvedCustomerName;
  let resolvedSeller;
  let resolvedAvor;
  let resolvedSellerUserId;
  let resolvedAvorUserId;
  const initialSellerUserId = options.sellerUserId ?? null;
  const initialAvorUserId = options.avorUserId ?? null;
  try {
    S.project.customerName = customerName;
    S.project.seller = seller;
    S.project.avor = avor;
    S.project.sellerUserId = initialSellerUserId;
    S.project.avorUserId = initialAvorUserId;
    await ensureProjectUserAssignments('manual');
    resolvedCustomerName = S.project.customerName || '';
    resolvedSeller = S.project.seller || '';
    resolvedAvor = S.project.avor || '';
    resolvedSellerUserId = S.project.sellerUserId ?? null;
    resolvedAvorUserId = S.project.avorUserId ?? null;
  } finally {
    S.project.customerName = previousMeta.customerName;
    S.project.seller = previousMeta.seller;
    S.project.avor = previousMeta.avor;
    S.project.sellerUserId = previousMeta.sellerUserId;
    S.project.avorUserId = previousMeta.avorUserId;
  }

  let payload;
  try {
    payload = await fetch(apiUrl(`/api/orders/${numericOrderId}/meta`), {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        projectNumber,
        customerName: resolvedCustomerName,
        ...customerIdRequestFields(customerIdentity, options),
        ...customerContactRequestFields(customerContactIdentity, options),
        orderNumber,
        orderDescription,
        seller: resolvedSeller,
        avor: resolvedAvor,
        sellerUserId: resolvedSellerUserId,
        avorUserId: resolvedAvorUserId,
        ...OrderEditingSession.editingSessionRequestFields()
      })
    }).then(readJsonResponse);
  } catch (error) {
    if (await OrderEditingSession.handleOrderSaveConflict(error, { showDialog: true })) {
      error.orderEditingConflict = true;
    }
    throw error;
  }

  const order = { ...customerContactIdentity, ...(payload.order || {}) };
  S.project.projectNumber = normalizeText(order.projektnummer || projectNumber) || DEFAULT_PROJECT_NUMBER;
  applyOrderCustomerIdentity(order, resolvedCustomerName);
  S.project.orderNumber = normalizeText(order.auftragsnummer || orderNumber);
  S.project.orderDescription = normalizeText(order.bezeichnung ?? orderDescription);
  S.project.seller = normalizeText(order.verkaeufer_name || S.project.seller);
  S.project.avor = normalizeText(order.avor_name || S.project.avor);
  S.project.sellerUserId = order.verkaeufer_benutzer_id ?? S.project.sellerUserId ?? null;
  S.project.avorUserId = order.avor_benutzer_id ?? S.project.avorUserId ?? null;
  if (S.project.inputDrafts && typeof S.project.inputDrafts === 'object') {
    S.project.inputDrafts = {};
  }
  S.project.persistence = {
    ...(S.project.persistence || {}),
    auftragId: order.auftrag_id ?? numericOrderId,
    projektId: order.projekt_id ?? S.project.persistence?.projektId ?? null,
    stateId: payload.state_id ?? S.project.persistence?.stateId ?? null,
    isTemp: order.ist_temp === true,
    lastSavedAt: order.geaendert_am || new Date().toISOString()
  };
  if (!S.file) S.file = { name: '', dirty: false };
  S.file.dirty = false;
  syncTemporaryOrderHint();
  SystemRegistry.renderShell();
  AppRuntime.refresh(false);
  updateAutosaveStatus({
    state: 'saved',
    label: `Gespeichert ${formatSavedTime(S.project.persistence.lastSavedAt)}`,
    lastSavedAt: S.project.persistence.lastSavedAt,
    error: ''
  });
  setStatus(`Auftrag ${S.project.orderNumber} aktualisiert.`);
  rememberLastOpenedOrder(payload.order || {});
  return payload;
}

async function updateDossierFile(dossierFileId, data = {}) {
  const payload = await fetch(apiUrl(`/api/dossier-files/${encodeURIComponent(dossierFileId)}`), {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(data)
  }).then(readJsonResponse);
  return payload.file || null;
}

async function createDossierSnapshot(orderId, data = {}) {
  const numericOrderId = Number(orderId || S.project?.persistence?.auftragId || 0);
  if (!Number.isFinite(numericOrderId) || numericOrderId <= 0) {
    throw new Error('Bitte zuerst ein Dossier öffnen oder speichern.');
  }
  const payload = await fetch(apiUrl(`/api/dossiers/${numericOrderId}/snapshots`), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(data)
  }).then(readJsonResponse);
  return payload.file || null;
}

async function loadDossierSnapshots(orderId) {
  const numericOrderId = Number(orderId || S.project?.persistence?.auftragId || 0);
  if (!Number.isFinite(numericOrderId) || numericOrderId <= 0) return [];
  const payload = await fetch(apiUrl(`/api/dossiers/${numericOrderId}/snapshots`)).then(readJsonResponse);
  return Array.isArray(payload.snapshots) ? payload.snapshots : [];
}

function deleteDossierFile(dossierFileId) {
  return fetch(apiUrl(`/api/dossier-files/${encodeURIComponent(dossierFileId)}`), {
    method: 'DELETE'
  }).then(readJsonResponse);
}

function markProjectMetaDirty() {
  if (!S.file) S.file = { name: '', dirty: true };
  S.file.dirty = true;
  markProjectModelChanged('project-meta');
  scheduleInitialDrawAutosave();
  refreshDirtyStatus();
}

function initProjectPersistence() {
  if (initialized) return;
  initialized = true;
  ensureDefaultProjectContext();
  OrderEditingSession.initOrderEditingSession();
  refreshDirtyStatus();
  syncTemporaryOrderHint();
  window.addEventListener('ks:project-dirty', scheduleInitialDrawAutosave);
  window.setInterval(autosaveCurrentState, AUTOSAVE_INTERVAL_MS);
  window.setInterval(refreshDirtyStatus, 10000);
}

const ProjectPersistenceService = Object.freeze({
  DEFAULT_PROJECT_NUMBER,
  DEFAULT_TEMP_USER_LABEL,
  AUTOSAVE_INTERVAL_MS,
  isTempOrderNumber,
  initProjectPersistence,
  collectAppState,
  restoreAppState,
  autosaveCurrentState,
  manualSaveCurrentState,
  saveAsCurrentState,
  saveCurrentState,
  ensureCurrentOrderSaved,
  flushForMaintenance,
  searchProjects,
  searchCustomers,
  searchCustomerContacts,
  searchPersons,
  createCustomer,
  createCustomerContact,
  getCustomerContactMergePreview,
  mergeCustomerContact,
  searchOrders,
  resolveOrderNumber,
  searchUsers,
  loadOrdersForProject,
  loadOrderByProjectOrder,
  loadOrderWithUnsavedGuard,
  readLastOpenedOrder,
  rememberLastOpenedOrder,
  autoOpenLastOrderIfEnabled,
  startNewDetachedProject,
  currentOrderMeta,
  loadOrderDossier,
  getOrderDeletePreview,
  deleteCurrentOrder,
  updateCurrentOrderMeta,
  createDossierSnapshot,
  loadDossierSnapshots,
  updateDossierFile,
  deleteDossierFile,
  hasProjectDrawingContent,
  hasSavableProjectContent,
  markProjectMetaDirty
});

export {
  ProjectPersistenceService,
  isTempOrderNumber,
  initProjectPersistence,
  collectAppState,
  restoreAppState,
  autosaveCurrentState,
  manualSaveCurrentState,
  saveAsCurrentState,
  saveCurrentState,
  ensureCurrentOrderSaved,
  flushForMaintenance,
  searchProjects,
  searchCustomers,
  searchCustomerContacts,
  searchPersons,
  createCustomer,
  createCustomerContact,
  getCustomerContactMergePreview,
  mergeCustomerContact,
  searchOrders,
  resolveOrderNumber,
  searchUsers,
  loadOrdersForProject,
  loadOrderByProjectOrder,
  loadOrderWithUnsavedGuard,
  readLastOpenedOrder,
  rememberLastOpenedOrder,
  autoOpenLastOrderIfEnabled,
  startNewDetachedProject,
  currentOrderMeta,
  loadOrderDossier,
  getOrderDeletePreview,
  deleteCurrentOrder,
  updateCurrentOrderMeta,
  createDossierSnapshot,
  loadDossierSnapshots,
  updateDossierFile,
  deleteDossierFile,
  hasProjectDrawingContent,
  hasSavableProjectContent,
  markProjectMetaDirty
};
